import {
  LoadingModal,
  useProductRelations,
} from 'components/products/relations';
import { LoadingSpinner } from 'components/ui';
import { formatDistanceStrict, parse } from 'date-fns';
import type { ProductCrossSell } from 'store/services/types';
import { useParseURLQueryParams } from 'utils';
import { ProductImage } from '../ProductImage';
import { SaveSegment } from './CrossSellSaveSegment';

export const CrossSell = () => {
  const { rawQueryParams } = useParseURLQueryParams();
  const { start_date, end_date } = rawQueryParams;

  const { selectedProduct, crossSell, crossSellIsLoading } =
    useProductRelations();

  const dateRangeDistance =
    start_date &&
    end_date &&
    formatDistanceStrict(
      parse(start_date, 'yyyy-MM-dd', new Date()),
      parse(end_date, 'yyyy-MM-dd', new Date())
    );

  return (
    <div className="">
      <LoadingModal loading={crossSellIsLoading} />
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xl font-bold">Cross sell </p>
          <p className="font-normal text-gray-500 text-segments-13px">
            Customers who bought{' '}
            <span className="font-bold">{selectedProduct?.title}</span> in the
            last
            <span> {dateRangeDistance || '...'}</span> also bought:
          </p>
        </div>
      </div>
      <div className="mt-4 ">
        {crossSellIsLoading ? (
          <div className="flex items-center justify-center w-full h-48 ">
            <LoadingSpinner className="w-16 h-16 text-segments-accent-color-orange" />
          </div>
        ) : (
          <div className="flex space-x-4">
            {crossSell?.length ? (
              crossSell.map((item) => (
                <CrossSellCard key={item.id} product={item} />
              ))
            ) : (
              <div className="flex items-center justify-center w-full h-48 rounded-lg bg-segments-gray-F8">
                <p className="text-gray-500">No product found</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

function CrossSellCard({ product }: { product: ProductCrossSell }) {
  const { selectedProduct } = useProductRelations();

  return (
    <div className="flex flex-col justify-between px-4 pt-3 pb-2 rounded-lg w-segments-165px min-w-segments-165px bg-segments-gray-F8">
      <div>
        <ProductImage
          productId={product.id}
          width="56"
          height="56"
          className="object-contain bg-white rounded-md h-14 w-14"
          alt={product.title || ''}
        />
        <div className="flex items-center mt-2 space-x-2">
          <p className="text-2xl leading-7">
            {product.likely_to_buy_rate.toLocaleString('en', {
              style: 'percent',
            })}
          </p>
          <p className="text-black text-opacity-50 text-segments-13px">
            of customers
          </p>
        </div>
        <div className="mt-1.5">
          <p className="font-bold leading-4 text-segments-13px line-clamp-2">
            {product.title}
          </p>
        </div>
      </div>
      <div>
        <div className="w-full h-px mt-3 bg-gray-200" />
        <div className="relative mt-1">
          <SaveSegment
            productTitle={selectedProduct?.title}
            productTitleExclude={product.title}
          />
        </div>
      </div>
    </div>
  );
}
